import type { FeaturedWallet } from "./types"
import type { WalletChainId } from "../config"

type WalletWindow = Window & {
  tronLink?: unknown
  tronWeb?: unknown
  tokenpocket?: unknown
  okxwallet?: unknown
  trustwallet?: unknown
  BinanceChain?: unknown
  bybitWallet?: unknown
  bitkeep?: unknown
  bitget?: unknown
  imToken?: unknown
  SafePalHook?: unknown
  sfpPlatform?: unknown
  phantom?: unknown
}

const MOBILE_USER_AGENT = /android|iphone|ipad|ipod|harmony/i
const WALLET_BROWSER_USER_AGENT =
  /tokenpocket|trust(?:wallet)?|imtoken|ok(?:app|x)|binance|bybit|bit(?:keep|get)|tronlink|safepal|mytonwallet|mywallet|phantom/i

function getWalletWindow(): WalletWindow | undefined {
  if (typeof window === "undefined") return undefined
  return window as WalletWindow
}

export function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false
  return MOBILE_USER_AGENT.test(navigator.userAgent)
}

export function isWalletBrowserEnvironment(): boolean {
  const w = getWalletWindow()
  if (!w || typeof navigator === "undefined") return false

  const hasInjectedProvider = Boolean(
    w.tronLink ||
      w.tronWeb ||
      w.tokenpocket ||
      w.okxwallet ||
      w.trustwallet ||
      w.BinanceChain ||
      w.bybitWallet ||
      w.bitkeep ||
      w.bitget ||
      w.imToken ||
      w.SafePalHook ||
      w.phantom,
  )

  return (
    hasInjectedProvider || WALLET_BROWSER_USER_AGENT.test(navigator.userAgent)
  )
}

export function shouldRedirectToWalletBrowser(): boolean {
  return isMobileBrowser() && !isWalletBrowserEnvironment()
}

function hasGlobalWalletObject(walletId: string): boolean {
  const w = getWalletWindow()
  if (!w) return false

  switch (walletId) {
    case "binance":
      return Boolean(w.BinanceChain)
    case "okx":
      return Boolean(w.okxwallet)
    case "trust":
      return Boolean(w.trustwallet)
    case "bybit":
      return Boolean(w.bybitWallet)
    case "bitget":
      return Boolean(w.bitkeep || w.bitget)
    case "imtoken":
      return Boolean(w.imToken)
    case "tokenpocket":
      return Boolean(w.tokenpocket)
    case "tronlink":
      return Boolean(w.tronLink || w.tronWeb)
    case "safepal":
      return Boolean(w.SafePalHook || w.sfpPlatform)
    case "phantom":
      return Boolean(w.phantom)
    case "mywallet":
      return /mytonwallet|mywallet/i.test(navigator.userAgent)
    default:
      return false
  }
}

function hasDualChainTron(walletId: string): boolean {
  const w = getWalletWindow()
  if (!w) return false
  if (w.tronLink || w.tronWeb) return true
  return hasGlobalWalletObject(walletId)
}

function hasTronProvider(provider?: FeaturedWallet["tronProvider"]): boolean {
  const w = getWalletWindow()
  if (!w) return false

  switch (provider) {
    case "tokenpocket":
      return Boolean(w.tokenpocket || w.tronLink || w.tronWeb)
    case "trust":
      return hasDualChainTron("trust")
    case "imtoken":
      return Boolean(w.tronWeb || w.tronLink || w.imToken)
    case "okx":
      return hasDualChainTron("okx")
    case "binance":
      return hasDualChainTron("binance")
    case "bitget":
      return hasDualChainTron("bitget")
    case "bybit":
      return hasDualChainTron("bybit")
    case "safepal":
      return Boolean(w.SafePalHook || w.tronLink || w.tronWeb)
    case "mywallet":
    case "phantom":
      // Prefer real TRON injection; otherwise treat as not installed so
      // mobile flow can deeplink into the wallet browser.
      return Boolean(w.tronLink || w.tronWeb)
    case "tronlink":
    default:
      return Boolean(w.tronLink || w.tronWeb)
  }
}

export function walletSupportsTron(wallet: FeaturedWallet): boolean {
  return wallet.chain === "tron" || Boolean(wallet.tronProvider)
}

export function getSupportedConnectChains(
  _wallet: FeaturedWallet,
): WalletChainId[] {
  void _wallet
  return ["tron"]
}

export function resolveConnectChain(
  _wallet: FeaturedWallet,
  _activeChain?: WalletChainId,
): "tron" {
  void _wallet
  void _activeChain
  return "tron"
}

export function isWalletInstalled(
  wallet: FeaturedWallet,
  _connectors?: unknown,
  _options?: { activeChain?: WalletChainId },
): boolean {
  void _connectors
  void _options
  return hasTronProvider(wallet.tronProvider ?? "tronlink")
}
